import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Loader2, Search, Send, Share2, Users, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest } from "@/lib/queryClient";
import { useHubRole } from "@/hooks/use-hub-role";
import {
  communitySharePayload,
  resourceShareContent,
  RESOURCE_TYPE_LABELS,
  shouldCloseShareDialog,
  type ResourceDescriptor,
} from "./resource-share";
import { rollbackNewTimerShareAfterDeliveryFailure } from "./timer-share-rollback";

interface Contact {
  id: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
}

type DeliveryStatus = "idle" | "pending" | "success" | "error";

interface Conversation {
  id: string;
  hubId: string;
}

export interface ResourceShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hubId: string;
  resource: ResourceDescriptor;
  canDirectShare?: boolean;
}

/**
 * Shared dialog for publishing a resource to Community and/or sending it to
 * accepted Village contacts. Each destination is deliberately independent so
 * a failed recipient can be retried without duplicating completed shares.
 */
export function ResourceShareDialog({
  open,
  onOpenChange,
  hubId,
  resource,
  canDirectShare = false,
}: ResourceShareDialogProps) {
  const hubRole = useHubRole(hubId, open);
  const canShareToCommunity = ["owner", "manager", "collaborator"].includes(hubRole || "");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [recipientPickerOpen, setRecipientPickerOpen] = useState(false);
  const [deliveryId, setDeliveryId] = useState(() => crypto.randomUUID());
  const [shareToCommunity, setShareToCommunity] = useState(true);
  const [shareToMessages, setShareToMessages] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [communityStatus, setCommunityStatus] = useState<DeliveryStatus>("idle");
  const [recipientStatus, setRecipientStatus] = useState<Record<string, DeliveryStatus>>({});

  const contactsQuery = useQuery<Contact[]>({
    queryKey: [`/api/messenger/${hubId}/contacts`],
    enabled: open && Boolean(hubId),
    staleTime: 10_000,
  });

  const resetTemporaryState = useCallback(() => {
    setMessage("");
    setSearch("");
    setRecipientPickerOpen(false);
    setDeliveryId(crypto.randomUUID());
    setShareToCommunity(true);
    setShareToMessages(false);
    setSelectedIds([]);
    setCommunityStatus("idle");
    setRecipientStatus({});
  }, []);

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    if (!nextOpen) resetTemporaryState();
    onOpenChange(nextOpen);
  }, [onOpenChange, resetTemporaryState]);

  useEffect(() => {
    if (!open) resetTemporaryState();
  }, [open, resource.id, resetTemporaryState]);

  const contacts = contactsQuery.data || [];
  const selectedContacts = contacts.filter((contact) => selectedIds.includes(contact.id));
  const visibleContacts = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return contacts;
    return contacts.filter((contact) =>
      [contact.displayName, contact.username].some((value) => value.toLowerCase().includes(needle)),
    );
  }, [contacts, search]);

  const canSendToMessages = resource.type !== "timer" || canDirectShare;
  const communitySelected = canShareToCommunity && shareToCommunity;
  const hasConfiguredTarget = communitySelected || (canSendToMessages && shareToMessages && selectedIds.length > 0);
  const hasPending = communityStatus === "pending" || Object.values(recipientStatus).some((status) => status === "pending");
  const hasFailedDelivery = communityStatus === "error" || selectedIds.some((id) => recipientStatus[id] === "error");
  const hasWorkToSend =
    (communitySelected && communityStatus !== "success") ||
    (canSendToMessages && shareToMessages && selectedIds.some((id) => recipientStatus[id] !== "success"));

  const toggleRecipient = (id: string) => {
    setSelectedIds((current) => current.includes(id) ? current.filter((selected) => selected !== id) : [...current, id]);
  };

  const sendToRecipient = async (contact: Contact): Promise<boolean> => {
    setRecipientStatus((current) => ({ ...current, [contact.id]: "pending" }));
    let timerShare: { share: { id: string }; created: boolean; sourceHubId: string } | null = null;
    try {
      if (resource.type === "timer") {
        if (!canDirectShare) {
          throw new Error("Only the timer owner can share it directly.");
        }
        // Establish the owner-authorized, view-only relationship before
        // creating a Messenger conversation or message.
        timerShare = await apiRequest<{ share: { id: string }; created: boolean; sourceHubId: string }>("/api/timer-shares", {
          method: "POST",
          body: JSON.stringify({
            timerId: resource.id,
            sharedWithUserId: contact.id,
          }),
        });
      }

      const conversation = await apiRequest<Conversation>(`/api/messenger/${hubId}/conversations`, {
        method: "POST",
        body: JSON.stringify({ participantId: contact.id }),
      });
      await apiRequest(`/api/messenger/${conversation.hubId || hubId}/conversations/${conversation.id}/messages`, {
        method: "POST",
        body: JSON.stringify({
          content: resourceShareContent(resource, message),
          messageType: "resource_share",
          metadata: {
            resourceType: resource.type,
            resourceId: resource.id,
            title: resource.title,
            description: resource.description,
            imageUrl: resource.imageUrl || undefined,
            deliveryId: `${deliveryId}:${contact.id}`,
          },
        }),
      });
      setRecipientStatus((current) => ({ ...current, [contact.id]: "success" }));
      return true;
    } catch (error) {
      if (timerShare) {
        try {
          await rollbackNewTimerShareAfterDeliveryFailure(
            timerShare,
            (url) => apiRequest(url, { method: "DELETE" }),
          );
        } catch (rollbackError) {
          console.error("[ResourceShareDialog] Could not roll back timer share after message failure:", rollbackError);
        }
      }
      console.error("[ResourceShareDialog] Recipient share failed:", error);
      setRecipientStatus((current) => ({ ...current, [contact.id]: "error" }));
      return false;
    }
  };

  const share = async () => {
    if (!hasConfiguredTarget || hasPending || !hasWorkToSend) return;

    const jobs: Promise<boolean>[] = [];
    if (communitySelected && communityStatus !== "success") {
      setCommunityStatus("pending");
      jobs.push(
        apiRequest("/api/community/share", {
          method: "POST",
          body: JSON.stringify({
            ...communitySharePayload(resource, message),
            hubId,
            deliveryId: `${deliveryId}:community`,
          }),
        }).then(
          () => {
            setCommunityStatus("success");
            return true;
          },
          () => {
            setCommunityStatus("error");
            return false;
          },
        ),
      );
    }
    if (canSendToMessages && shareToMessages) {
      selectedContacts
        .filter((contact) => recipientStatus[contact.id] !== "success")
        .forEach((contact) => jobs.push(sendToRecipient(contact)));
    }
    const deliveryResults = await Promise.all(jobs);
    if (shouldCloseShareDialog(deliveryResults)) {
      handleOpenChange(false);
    }
  };

  const statusLabel = (status: DeliveryStatus) => {
    if (status === "pending") return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
    if (status === "success") return <Check className="h-4 w-4 text-green-600" />;
    if (status === "error") return <span className="text-xs font-medium text-destructive">Failed</span>;
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share {RESOURCE_TYPE_LABELS[resource.type]}
          </DialogTitle>
          <DialogDescription>Choose where to share “{resource.title}”.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            {resource.imageUrl ? (
              <img src={resource.imageUrl} alt="" className="h-16 w-16 shrink-0 rounded-md object-cover" />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-gray-200">
                <Share2 className="h-7 w-7 text-gray-400" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium text-gray-900">{resource.title}</p>
              <p className="text-sm text-gray-600">{RESOURCE_TYPE_LABELS[resource.type]}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="resource-share-message">Add a message (optional)</Label>
            <Textarea id="resource-share-message" value={message} onChange={(event) => setMessage(event.target.value)} placeholder={`Check out this ${RESOURCE_TYPE_LABELS[resource.type].toLowerCase()}: ${resource.title}`} className="min-h-[80px]" />
          </div>

          <div className="space-y-3 border-t pt-4">
            {canShareToCommunity && (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2"><Users className="h-4 w-4 text-blue-600" /><Label htmlFor="share-community">Share to Community Feed</Label></div>
                <div className="flex items-center gap-2">{statusLabel(communityStatus)}<Switch id="share-community" checked={shareToCommunity} onCheckedChange={setShareToCommunity} /></div>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2"><Send className="h-4 w-4 text-green-600" /><Label htmlFor="share-messages">Send via Messages</Label></div>
              <Switch id="share-messages" checked={shareToMessages} onCheckedChange={setShareToMessages} disabled={!canSendToMessages} />
            </div>
            {resource.type === "timer" && !canDirectShare && (
              <p className="text-sm text-muted-foreground">
                Only the timer owner can share it directly with contacts.
                {canShareToCommunity ? " Community sharing remains available." : " Community sharing is unavailable for this Hub role."}
              </p>
            )}
          </div>

          {shareToMessages && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label>Village members</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setRecipientPickerOpen((current) => !current)}>
                  <Users className="h-4 w-4" />
                  {recipientPickerOpen ? "Hide Friends" : "Select Friends"}
                </Button>
              </div>
              {selectedContacts.length > 0 && <div className="flex flex-wrap gap-2">{selectedContacts.map((contact) => <Button key={contact.id} type="button" size="sm" variant="secondary" onClick={() => toggleRecipient(contact.id)}>{contact.displayName}<X className="h-3 w-3" /></Button>)}</div>}
              {recipientPickerOpen && (
                <>
                  <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9" placeholder="Search accepted contacts" /></div>
                  <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border p-1">
                    {contactsQuery.isLoading && <p className="p-2 text-sm text-muted-foreground">Loading contacts…</p>}
                    {contactsQuery.isError && <p className="p-2 text-sm text-destructive">Could not load accepted contacts.</p>}
                    {!contactsQuery.isLoading && !contactsQuery.isError && visibleContacts.length === 0 && <p className="p-2 text-sm text-muted-foreground">No accepted Village members yet.</p>}
                    {visibleContacts.map((contact) => <button key={contact.id} type="button" onClick={() => toggleRecipient(contact.id)} className="flex w-full items-center gap-3 rounded px-2 py-2 text-left hover:bg-accent"><span className={`flex h-4 w-4 items-center justify-center rounded border ${selectedIds.includes(contact.id) ? "border-primary bg-primary text-primary-foreground" : ""}`}>{selectedIds.includes(contact.id) && <Check className="h-3 w-3" />}</span>{contact.avatarUrl && <img src={contact.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />}<span className="flex-1 text-sm">{contact.displayName}</span>{statusLabel(recipientStatus[contact.id] || "idle")}</button>)}
                  </div>
                </>
              )}
            </div>
          )}

          {!hasConfiguredTarget && <p className="text-sm text-destructive">Choose Community or at least one message recipient.</p>}
          {hasFailedDelivery && <p className="text-sm text-destructive">Some shares failed. Retry will send only failed destinations.</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={share} disabled={!hasConfiguredTarget || hasPending || !hasWorkToSend}>
            {hasPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
            {hasFailedDelivery ? "Retry failed" : "Share"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ResourceShareDialog;